import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createApiClient, buildBackendUrl } from '@textile-erp/core';

// Types for pending approval notifications
export type PendingApprovalType = 'LOAN' | 'PERMISSION' | 'ADVANCE' | 'JOB_CHANGE';

export interface PendingApprovalItem {
  id: string;
  type: PendingApprovalType;
  date: string;
  amount?: number;
  description: string;
  employee: {
    id: string;
    code: string;
    name: string;
    profilePictureUrl: string | null;
  };
  createdAt: string;
}

export interface PendingApprovalCounts {
  loan: number;
  permission: number;
  advance: number;
  jobChange: number;
  total: number;
}

interface NotificationState {
  counts: PendingApprovalCounts;
  pendingApprovals: PendingApprovalItem[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

interface NotificationContextType extends NotificationState {
  refreshNotifications: () => Promise<void>;
  fetchPendingApprovals: () => Promise<void>;
  decrementCount: (type: PendingApprovalType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const apiClient = createApiClient(buildBackendUrl('/api'));

const initialCounts: PendingApprovalCounts = {
  loan: 0,
  permission: 0,
  advance: 0,
  jobChange: 0,
  total: 0,
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<NotificationState>({
    counts: initialCounts,
    pendingApprovals: [],
    hasMore: false,
    loading: false,
    error: null,
  });

  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef<number>(0);

  // Fetch just the counts (lightweight call for badge)
  const fetchCounts = useCallback(async () => {
    // Prevent duplicate calls within 1 second
    const now = Date.now();
    if (isFetchingRef.current || (now - lastFetchTimeRef.current < 1000)) {
      return;
    }

    isFetchingRef.current = true;
    lastFetchTimeRef.current = now;

    try {
      const response = await apiClient.get<PendingApprovalCounts>('/payroll/pending-approvals/count');
      setState(prev => ({
        ...prev,
        counts: response,
        error: null,
      }));
    } catch (error) {
      console.error('Failed to fetch pending approvals count:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to fetch notifications',
      }));
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch pending approvals list (for dropdown)
  const fetchPendingApprovals = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      console.log('NotificationProvider: Fetching pending approvals...');
      const response = await apiClient.get<{
        data: PendingApprovalItem[];
        counts: PendingApprovalCounts;
        hasMore: boolean;
      }>('/payroll/pending-approvals/notifications');

      console.log('NotificationProvider: Response received:', {
        dataCount: response.data?.length ?? 0,
        counts: response.counts,
        hasMore: response.hasMore,
        rawResponse: response,
      });

      setState(prev => ({
        ...prev,
        pendingApprovals: response.data,
        counts: response.counts,
        hasMore: response.hasMore,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch notifications',
      }));
    }
  }, []);

  // Refresh all notification data
  const refreshNotifications = useCallback(async () => {
    await fetchCounts();
  }, [fetchCounts]);

  // Decrement count locally (optimistic update after approval)
  const decrementCount = useCallback((type: PendingApprovalType) => {
    setState(prev => {
      const typeKey = type === 'JOB_CHANGE' ? 'jobChange' : type.toLowerCase() as keyof Omit<PendingApprovalCounts, 'total'>;
      const newCounts = {
        ...prev.counts,
        [typeKey]: Math.max(0, prev.counts[typeKey] - 1),
        total: Math.max(0, prev.counts.total - 1),
      };
      return {
        ...prev,
        counts: newCounts,
      };
    });
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Listen for approval events to refresh count
  useEffect(() => {
    const handleApproval = () => {
      // Small delay to ensure database is updated
      setTimeout(() => {
        fetchCounts();
      }, 500);
    };

    // Listen for all approval types
    window.addEventListener('loan-approved', handleApproval);
    window.addEventListener('permission-approved', handleApproval);
    window.addEventListener('advance-approved', handleApproval);
    window.addEventListener('job-change-approved', handleApproval);
    window.addEventListener('approval-updated', handleApproval);

    return () => {
      window.removeEventListener('loan-approved', handleApproval);
      window.removeEventListener('permission-approved', handleApproval);
      window.removeEventListener('advance-approved', handleApproval);
      window.removeEventListener('job-change-approved', handleApproval);
      window.removeEventListener('approval-updated', handleApproval);
    };
  }, [fetchCounts]);

  // Poll for updates every 5 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchCounts();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [fetchCounts]);

  const value: NotificationContextType = {
    ...state,
    refreshNotifications,
    fetchPendingApprovals,
    decrementCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}
